import type { AppMountContext } from "@tc-superpods/app-contract";

function normalizePathname(pathname: string): string {
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;

  return normalized === "/" ? normalized : normalized.replace(/\/+$/u, "");
}

export const mode: AppMountContext["mode"] = __KORENEXT_MODE__;
export const authMode: "oidc" | "none" = __KORENEXT_AUTH_MODE__;
export const anonymousToken = __KORENEXT_ANON_TOKEN__;
export const oidcAuthority = __KORENEXT_OIDC_AUTHORITY__.replace(/\/+$/u, "");
export const oidcClientId = __KORENEXT_OIDC_CLIENT_ID__;
export const oidcRedirectPath = normalizePathname(__KORENEXT_OIDC_REDIRECT_PATH__);
export const oidcSilentRedirectPath = normalizePathname(__KORENEXT_OIDC_SILENT_REDIRECT_PATH__);
export const oidcScope = __KORENEXT_OIDC_SCOPE__;
export type UserpilotConfig = Readonly<{
  token: string;
}>;

const configuredUserpilot = {
  token: typeof __KORENEXT_USERPILOT_TOKEN__ === "undefined" ? "" : __KORENEXT_USERPILOT_TOKEN__
};

export const userpilotConfig: UserpilotConfig | undefined = configuredUserpilot.token.trim() === ""
  ? undefined
  : configuredUserpilot;

export type NewRelicConfig = Readonly<{
  accountId: string;
  agentId: string;
  applicationId: string;
  licenseKey: string;
  trustKey: string;
  beacon: string;
  errorBeacon: string;
}>;

const configuredNewRelic = {
  accountId: typeof __KORENEXT_NEW_RELIC_ACCOUNT_ID__ === "undefined" ? "" : __KORENEXT_NEW_RELIC_ACCOUNT_ID__,
  agentId: typeof __KORENEXT_NEW_RELIC_AGENT_ID__ === "undefined" ? "" : __KORENEXT_NEW_RELIC_AGENT_ID__,
  applicationId:
    typeof __KORENEXT_NEW_RELIC_APPLICATION_ID__ === "undefined"
      ? ""
      : __KORENEXT_NEW_RELIC_APPLICATION_ID__,
  licenseKey:
    typeof __KORENEXT_NEW_RELIC_LICENSE_KEY__ === "undefined" ? "" : __KORENEXT_NEW_RELIC_LICENSE_KEY__,
  trustKey: typeof __KORENEXT_NEW_RELIC_TRUST_KEY__ === "undefined" ? "" : __KORENEXT_NEW_RELIC_TRUST_KEY__,
  beacon: typeof __KORENEXT_NEW_RELIC_BEACON__ === "undefined" ? "" : __KORENEXT_NEW_RELIC_BEACON__,
  errorBeacon:
    typeof __KORENEXT_NEW_RELIC_ERROR_BEACON__ === "undefined"
      ? ""
      : __KORENEXT_NEW_RELIC_ERROR_BEACON__
};

export const newRelicConfig: NewRelicConfig | undefined = Object.values(configuredNewRelic).every(
  (value) => value.trim() !== ""
)
  ? configuredNewRelic
  : undefined;
export const includeRegistryFallback = __KORENEXT_INCLUDE_REGISTRY_FALLBACK__;
export const useTenantRegistryApi = mode === "production" ? true : __KORENEXT_USE_TENANT_REGISTRY_API__;
export const registryApiBaseUrl = normalizeRegistryApiBaseUrl(__KORENEXT_REGISTRY_API_BASE_URL__);
export const publishedRegistryUrl = "/registry.json";
export const legacyTenantId = "dev-tenant-01";
export const legacyTenantName = "KoreNext Dev";

export const localRegistryUrls = includeRegistryFallback
  ? ([
      // App-owned registry fragments come first: an app dev server that serves its own
      // registry.json (Manage on 5600) wins over the generic dev ports and the shell's copy.
      "http://127.0.0.1:5600/registry.json",
      "http://localhost:4200/registry.json",
      "http://localhost:5173/registry.json",
      "http://localhost:4173/registry.json",
      "http://localhost:4000/registry.json"
    ] as const)
  : [];

export const notificationExitDurationMs = 180;
export const standardNotificationDurationMs = 3_000;
export const errorNotificationDurationMs = 5_000;
export const sidebarCollapsedStorageKey = "kn-sidebar-collapsed";

function normalizeRegistryApiBaseUrl(value: string): string {
  try {
    const url = new URL(value);
    const isLoopback =
      url.hostname === "127.0.0.1" ||
      url.hostname === "localhost" ||
      url.hostname === "[::1]";
    const secureDeployment = url.protocol === "https:";
    const localDevelopment = mode !== "production" && isLoopback && url.protocol === "http:";
    if (!secureDeployment && !localDevelopment) throw new Error("Registry API URL must use HTTPS or local loopback HTTP.");
    if (!useTenantRegistryApi && !isLoopback) throw new Error("Local Registry API URL must use a loopback hostname.");
    if (url.username || url.password || url.search || url.hash) throw new Error("Registry API URL cannot contain credentials, a query, or a fragment.");

    return url.href.replace(/\/+$/u, "");
  } catch {
    throw new Error("KoreNext Registry API URL is invalid.");
  }
}

export { normalizePathname };
